import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, Check, AlertTriangle, ShieldAlert, Clock, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_CFG = {
  kirmizi_etiket: { icon: ShieldAlert, color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/40' },
  sla_ihlal: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/40' },
  kontrol_yaklasti: { icon: Clock, color: 'text-orange-400', bg: 'bg-orange-900/20 border-orange-800/40' },
  kontrol_gecikti: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/20 border-red-800/40' },
  eskalasyon_1: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-900/20 border-orange-800/40' },
  eskalasyon_2: { icon: ShieldAlert, color: 'text-red-500', bg: 'bg-red-900/30 border-red-700/50' },
  sozlesme_bitis: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-900/20 border-yellow-800/40' },
  aku_omur_uyari: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-900/20 border-blue-800/40' }
};

const DEFAULT_CFG = { icon: Bell, color: 'text-gray-400', bg: 'bg-gray-800/50 border-gray-700' };

export default function PKBildirimler() {
  const [bildirimler, setBildirimler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBildirimler(); }, []);

  const fetchBildirimler = async () => {
    try {
      const res = await axios.get(`${API}/pk/bildirimler`, { withCredentials: true });
      setBildirimler(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleOku = async (id) => {
    await axios.put(`${API}/pk/bildirimler/${id}/oku`, {}, { withCredentials: true });
    setBildirimler(prev => prev.map(b => b.id === id ? { ...b, okundu: true } : b));
  };

  const handleTumunuOku = async () => {
    const okunmamislar = bildirimler.filter(b => !b.okundu);
    await Promise.all(okunmamislar.map(b => axios.put(`${API}/pk/bildirimler/${b.id}/oku`, {}, { withCredentials: true })));
    setBildirimler(prev => prev.map(b => ({ ...b, okundu: true })));
  };

  const okunmamisSayisi = bildirimler.filter(b => !b.okundu).length;

  const formatTarih = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return 'az önce';
      if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
      return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  if (loading) return <div className="text-gray-400 text-center py-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="pk-bildirimler">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><Bell className="w-5 h-5 text-blue-400" /> Bildirimler</h2>
          {okunmamisSayisi > 0 && <Badge className="bg-red-600 text-white">{okunmamisSayisi} okunmamış</Badge>}
        </div>
        {okunmamisSayisi > 0 && (
          <Button onClick={handleTumunuOku} variant="outline" size="sm" className="bg-gray-800 border-gray-700 text-gray-300 hover:text-white h-8 text-xs">
            <Check className="w-3 h-3 mr-1" /> Tümünü Okundu İşaretle
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {bildirimler.length === 0 ? (
          <Card className="bg-gray-900/80 border-gray-700/50"><CardContent className="py-8 text-center text-gray-500">Henüz bildirim yok</CardContent></Card>
        ) : bildirimler.map(b => {
          const cfg = TIP_CFG[b.bildirim_tipi] || DEFAULT_CFG;
          const Icon = cfg.icon;
          return (
            <Card key={b.id} className={`border ${cfg.bg} ${b.okundu ? 'opacity-60' : ''}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className={`text-sm font-medium ${b.okundu ? 'text-gray-400' : 'text-white'}`}>{b.baslik}</p>
                      {!b.okundu && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-gray-400">{b.mesaj}</p>
                    <p className="text-[10px] text-gray-600 mt-1">{formatTarih(b.created_at)}</p>
                  </div>
                  {!b.okundu && (
                    <Button variant="ghost" size="sm" onClick={() => handleOku(b.id)} className="h-7 text-xs text-gray-500 hover:text-white">
                      <Check className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
